import { createContext, useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from './use-local-storage';

export const PLAYBOOK_CHANNEL = 'playbook-creators';

export const UnreadPlaybookChannelContext = createContext<
  | {
      unread: boolean;
      setUnread: (val: boolean) => void;
    }
  | undefined
>(undefined);

export const UnreadPlaybookChannelProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [unread, setUnread] = useState(false);
  const value = { unread, setUnread };
  return (
    <UnreadPlaybookChannelContext.Provider value={value}>
      {children}
    </UnreadPlaybookChannelContext.Provider>
  );
};

export const usePlaybookChannel = (chatClient) => {
  const [unread, setUnread] = useState(false);
  const [playbookLastMessageAt, setPlaybookLastMessageAt] = useLocalStorage(
    'playbook_last_message_at',
    null,
  );

  const markUnread = useCallback(() => {
    setUnread(true);
    setPlaybookLastMessageAt(new Date().toISOString());
  }, []);

  const markRead = useCallback(() => {
    setUnread(false);
    setPlaybookLastMessageAt(new Date().toISOString());
  }, []);

  useEffect(
    function handleChannelReadStatus() {
      if (!chatClient) {
        return;
      }

      const getUserChannels = async () => {
        const filter = { type: 'messaging', id: PLAYBOOK_CHANNEL };

        const [playbookChannel] = await chatClient.queryChannels(filter, [], {
          watch: true,
          state: true,
        });

        await playbookChannel.watch();

        if (
          new Date(playbookChannel?.state.last_message_at) >
          new Date(playbookLastMessageAt)
        ) {
          setUnread(true);
        }

        playbookChannel.on((event) => {
          if (
            event.type === 'message.read' &&
            event.channel_id === PLAYBOOK_CHANNEL
          ) {
            markRead();
          }

          if (
            ['notification.message_new', 'message.new'].includes(event.type) &&
            event.channel_id === PLAYBOOK_CHANNEL &&
            event.user.id !== chatClient?.userID
          ) {
            markUnread();
          }
        });
      };

      getUserChannels();
    },
    [chatClient],
  );

  return { isUnread: unread, markRead };
};
